import React, { Suspense, lazy, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import RouteBranding from "./RouteBranding";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import CookieConsent from "./components/common/CookieConsent";
import AppVersionUpdate from "./components/common/AppVersionUpdate";
import { AppShellLoadingSkeleton } from "./components/dashboard/DashboardLoadingSkeleton";
import { auth, db } from "./services/firebase";

const LandingPage = lazy(() => import("./pages/LandingPage"));
const CompetitionAppEntry = lazy(() => import("./pages/CompetitionAppEntry"));
const CompetitionPage = lazy(() => import("./pages/CompetitionPage"));
const PublicCompetitionResultsPage = lazy(() => import("./pages/PublicCompetitionResultsPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const AppShell = lazy(() => import("./pages/AppShell"));
const CoachOverview = lazy(() => import("./pages/CoachOverview"));
const WeeklyFeedbackPage = lazy(() => import("./pages/WeeklyFeedbackPage"));
const WeeklyFeedbackQuestionsPage = lazy(
  () => import("./pages/WeeklyFeedbackQuestionsPage")
);
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const StrengthPilotDatenschutz = lazy(() => import("./pages/StrengthPilotDatenschutz"));
const StrengthPilotImpressum = lazy(() => import("./pages/StrengthPilotImpressum"));
const StrengthPilotAccountDeletion = lazy(() => import("./pages/StrengthPilotAccountDeletion"));
const Anmeldung = lazy(() => import("./pages/Anmeldung"));
const WettkampfInfo = lazy(() => import("./pages/WettkampfInfo"));
const WettkampfAusschreibung2026 = lazy(() => import("./pages/WettkampfAusschreibung2026"));
const OpenerSubmission = lazy(() => import("./pages/OpenerSubmission"));
const CompetitionRegistration2026 = lazy(() => import("./pages/CompetitionRegistration2026"));
const StrengthPilotLanding = lazy(() => import("./pages/StrengthPilotLanding"));
const SubscribePage = lazy(() => import("./pages/SubscribePage"));
const DndBoardSandbox = lazy(() => import("./components/dnd/DndBoardSandbox"));

/** true when served from the StrengthPilot product domain */
const IS_STRENGTHPILOT_HOST =
  typeof window !== "undefined" &&
  (window.location.hostname === "strengthpilot.web.app" ||
   window.location.hostname === "strengthpilot.firebaseapp.com" ||
   window.location.hostname === "strengthpilot.de");

const APP_STORE_URL = "https://apps.apple.com/de/app/strengthpilot/id6761358456?l=en-GB";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.strengthpilot.app";

function PrivateRoute({ children }) {
  const { user, loading, needsSubscription } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (needsSubscription) {
    return <Navigate to="/subscribe" replace />;
  }

  return children;
}

function RouteLoadingFallback() {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const isDashboardRoute =
    pathname === "/dashboard" ||
    pathname === "/athletes" ||
    pathname === "/settings" ||
    pathname.startsWith("/athlete/");

  return isDashboardRoute ? <AppShellLoadingSkeleton /> : <LoadingSpinner />;
}

function MobileDashboardBlocker() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed left-0 top-0 z-[2147483647] flex w-[100vw] items-center justify-center bg-[var(--app-bg)] text-[var(--text)] px-6 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      style={{ height: "calc(var(--app-vh, 1vh) * 100)", touchAction: "none" }}
      role="alert"
      aria-live="assertive"
    >
      <div className="w-full max-w-md rounded-2xl border border-[color:var(--border)] bg-[var(--surface-1)] p-6 text-center shadow-sm">
        <div className="text-lg font-semibold">Mobile not supported</div>
        <div className="mt-2 text-sm text-[var(--text-muted)]">
          This web app is optimized for laptop/desktop only. Please download the app or open this page on a laptop or
          desktop computer.
        </div>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-500"
          >
            App Store
          </a>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-xl border border-[color:var(--border)] bg-[var(--surface-2)] px-4 py-3 text-sm font-semibold text-[var(--text)] transition hover:bg-white/5"
          >
            Google Play
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DashboardRoute({ isMobileBlocked }) {
  const { profile, loading, accessInfo } = useAuth();
  const isStaff = (accessInfo?.competitionEventIds?.length || profile?.competitionAccessEventIds?.length) > 0;
  const isAdmin = profile?.isAdmin === true || profile?.role === "ADMIN";
  if (loading) return <AppShellLoadingSkeleton />;
  if ((isStaff && !isAdmin) || (isMobileBlocked && isAdmin)) return <Navigate to="/competition" replace />;
  if (isMobileBlocked) {
    return <MobileDashboardBlocker />;
  }

  return (
    <PrivateRoute>
      <AppShell />
    </PrivateRoute>
  );
}

function App() {
  const getIsMobileBlocked = () => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;

    const uaMobile = Boolean(navigator.userAgentData?.mobile);
    const ua = navigator.userAgent || "";
    const isPhoneUA = /Android.+Mobile|iPhone|iPod|Windows Phone|webOS|BlackBerry|IEMobile|Opera Mini|Mobi/i.test(ua);

    // Only block when the viewport is actually mobile-sized.
    // This lets users on iOS "Request Desktop Website" through if Safari provides a desktop-sized viewport,
    // even when the UA still contains iPhone/iPod.
    const isSmallViewport = window.matchMedia
      ? window.matchMedia("(max-width: 900px)").matches
      : window.innerWidth <= 900;
    const isCoarsePointer = window.matchMedia ? window.matchMedia("(pointer: coarse)").matches : false;

    const isProbablyMobile = uaMobile || isPhoneUA || isCoarsePointer;
    return isSmallViewport && isProbablyMobile;
  };

  const [isMobileBlocked, setIsMobileBlocked] = useState(getIsMobileBlocked);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      window.auth = auth;
      window.db = db;
    }
  }, []);

  useEffect(() => {
    const update = () => setIsMobileBlocked(getIsMobileBlocked());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  useEffect(() => {
    const setVhVar = () => {
      // iOS Safari: 100vh can be wrong due to browser chrome.
      // Using innerHeight keeps the overlay truly fullscreen.
      document.documentElement.style.setProperty("--app-vh", `${window.innerHeight * 0.01}px`);
    };
    setVhVar();
    window.addEventListener("resize", setVhVar);
    window.addEventListener("orientationchange", setVhVar);
    return () => {
      window.removeEventListener("resize", setVhVar);
      window.removeEventListener("orientationchange", setVhVar);
    };
  }, []);

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppVersionUpdate />
            <RouteBranding />
            <ErrorBoundary>
            <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
                <Route path="/" element={IS_STRENGTHPILOT_HOST ? <StrengthPilotLanding /> : <LandingPage />} />
                <Route path="/competition/open" element={<CompetitionAppEntry />} />
                <Route path="/competition" element={isMobileBlocked && process.env.REACT_APP_COMPETITION_NATIVE_INVITES === "true" ? <CompetitionAppEntry /> : <CompetitionPage />} />
                <Route path="/results/:eventId" element={<PublicCompetitionResultsPage />} />
                <Route path="/results" element={<PublicCompetitionResultsPage />} />
                <Route path="/wettkampf-ergebnisse" element={<PublicCompetitionResultsPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/subscribe" element={<SubscribePage />} />
                <Route
                  path="/dashboard"
                  element={
                    <DashboardRoute isMobileBlocked={isMobileBlocked} />
                  }
                />
                <Route
                  path="/athletes"
                  element={
                    <DashboardRoute isMobileBlocked={isMobileBlocked} />
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <DashboardRoute isMobileBlocked={isMobileBlocked} />
                  }
                />
                <Route
                  path="/athlete/:athleteId/:tab?"
                  element={
                    <DashboardRoute isMobileBlocked={isMobileBlocked} />
                  }
                />
                <Route
                  path="/coach"
                  element={
                    <PrivateRoute>
                      <CoachOverview />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/weekly-feedback-questions"
                  element={
                    <PrivateRoute>
                      <WeeklyFeedbackQuestionsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/weekly-feedback"
                  element={
                    <PrivateRoute>
                      <WeeklyFeedbackPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/dnd-board-sandbox"
                  element={
                    <PrivateRoute>
                      <DndBoardSandbox />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/impressum"
                  element={
                    IS_STRENGTHPILOT_HOST ? <StrengthPilotImpressum /> : <Impressum />
                  }
                />
                <Route
                  path="/datenschutz"
                  element={
                    IS_STRENGTHPILOT_HOST ? <StrengthPilotDatenschutz /> : <Datenschutz />
                  }
                />
                <Route
                  path="/konto-loeschen"
                  element={
                    IS_STRENGTHPILOT_HOST ? <StrengthPilotAccountDeletion /> : <Navigate to="/" replace />
                  }
                />
                <Route
                  path="/account-deletion"
                  element={
                    IS_STRENGTHPILOT_HOST ? <StrengthPilotAccountDeletion /> : <Navigate to="/" replace />
                  }
                />
                <Route path="/anmeldung" element={<Anmeldung />} />
                <Route path="/wettkampf-anmeldung" element={<CompetitionRegistration2026 />} />
                <Route path="/wettkampf-info" element={<WettkampfInfo />} />
                <Route path="/wettkampf-ausschreibung-2026" element={<WettkampfAusschreibung2026 />} />
                <Route path="/opener" element={<OpenerSubmission />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
            <CookieConsent />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
